import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { ToolbarButton } from '@ohif/ui';
import { utils } from '@ohif/core';
import Simple2DMPRViewport, { mprState } from '../Simple2DMPRViewport';
import { buildMPR, isCached } from '../mprBuilder';

const { studyMetadataManager } = utils;

function getImageIds(vsd, idx) {
  if (!vsd || !vsd[idx]) return [];
  const { displaySetInstanceUID: uid, StudyInstanceUID: suid } = vsd[idx];
  const study = studyMetadataManager.all().find(s => s.studyInstanceUID === suid);
  if (!study) return [];
  const ds = study._displaySets.find(d => d.displaySetInstanceUID === uid);
  if (!ds || !ds.images) return [];
  const ids = [];
  ds.images.forEach(img => {
    const n = img.numFrames;
    if (n > 1) for (let i = 0; i < n; i++) ids.push(img.getImageId(i));
    else ids.push(img.getImageId());
  });
  return ids;
}

const WL = {
  '1': { ww: 400,  wc: 40,   label: 'Soft tissue' },
  '2': { ww: 1500, wc: -600, label: 'Lung'        },
  '3': { ww: 150,  wc: 90,   label: 'Brain'       },
  '4': { ww: 2500, wc: 480,  label: 'Bone'        },
  '5': { ww: 80,   wc: 40,   label: 'Soft'        },
  '6': { ww: 40,   wc: 40,   label: 'Liver'       },
};

// Panel — uses portal just like Simple2DMPRLayout
function SinglePanel({ imageIds, displaySetUID, orientation, onClose }) {
  const [container, setContainer] = useState(null);
  const label = orientation === 'coronal' ? 'COR' : 'SAG';
  const color = orientation === 'coronal' ? '#4fc3f7' : '#ce93d8';

  useEffect(() => {
    // Clear all elements so ref lines don't draw (allMounted check in redrawOverlay)
    mprState.elements = {};
    const meta = window.__mprSliceCache && window.__mprSliceCache[displaySetUID + '_meta'];
    if (meta) {
      Object.assign(mprState, {
        depth: meta.depth, rows: meta.rows, columns: meta.columns,
        ww: meta.ww, wc: meta.wc,
        axialIdx: meta.axialMid, corIdx: meta.corMid, sagIdx: meta.sagMid,
      });
    }
    // Restore elements on unmount (in case 2D MPR opens after)
    return () => { mprState.elements = {}; };
  }, [displaySetUID]);

  useEffect(() => {
    // Same portal target as Simple2DMPRLayout
    const el =
      document.querySelector('[data-cy="viewport-container-0"]') ||
      document.querySelector('.viewport-drop-target') ||
      document.querySelector('.main-content') ||
      document.body;
    setContainer(el);
  }, []);

  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape') { onClose(); return; }
      if (WL[e.key]) {
        window.dispatchEvent(new CustomEvent('mprApplyWL', { detail: WL[e.key] }));
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  if (!container) return null;

  return ReactDOM.createPortal(
    <div style={{
      position: 'absolute', inset: 0, zIndex: 100,
      display: 'flex', flexDirection: 'column', background: '#111',
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4,
        padding: '3px 8px', background: '#1c1c1c',
        borderBottom: '1px solid #333', flexShrink: 0, minHeight: 30,
      }}>
        <span style={{ color, fontSize: 12, fontWeight: 'bold', marginRight: 6 }}>
          {label} View
        </span>
        {Object.entries(WL).map(([k, p]) => (
          <button key={k}
            title={`Keyboard: ${k}`}
            onClick={() => window.dispatchEvent(new CustomEvent('mprApplyWL', { detail: p }))}
            style={{
              background: '#2a2a2a', border: '1px solid #444', color: '#ddd',
              padding: '2px 8px', borderRadius: 3, cursor: 'pointer', fontSize: 11,
            }}>
            <b style={{ color }}>{k}</b> {p.label}
          </button>
        ))}
        <span style={{ color: '#444', fontSize: 10, marginLeft: 4 }}>
          scroll: mouse wheel · W/L: right drag
        </span>
        <div style={{ flex: 1 }} />
        <button onClick={onClose} style={{
          background: '#c0392b', border: 'none', color: '#fff',
          padding: '3px 12px', borderRadius: 3,
          cursor: 'pointer', fontSize: 12, fontWeight: 'bold',
        }}>✕ Exit</button>
      </div>
      {/* Single viewport — fills remaining space */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0 }}>
        <Simple2DMPRViewport
          imageIds={imageIds}
          orientation={orientation}
          displaySetUID={displaySetUID}
        />
      </div>
    </div>,
    container
  );
}

export default function Single2DMPRButton({
  parentContext, toolbarClickCallback, button, activeButtons, isActive,
}) {
  const { id, label, icon } = button;
  const orientation = button.orientation || 'coronal';

  const [open,     setOpen]     = useState(false);
  const [imageIds, setImageIds] = useState([]);
  const [status,   setStatus]   = useState('idle');
  const buildingFor = useRef(null);

  const { viewportSpecificData: vsd, activeViewportIndex: idx } = useSelector(state => ({
    viewportSpecificData: state.viewports.viewportSpecificData,
    activeViewportIndex:  state.viewports.activeViewportIndex,
  }));

  const getUID = () => vsd && vsd[idx] ? vsd[idx].displaySetInstanceUID : null;

  const tryBuild = uid => {
    if (!uid) return;
    if (isCached(uid)) { setStatus('ready'); return; }
    if (buildingFor.current === uid) return;
    const ids = getImageIds(vsd, idx);
    if (!ids.length) return;
    buildingFor.current = uid;
    setStatus('building');
    buildMPR(ids, uid).then(ok => {
      if (buildingFor.current !== uid) return;
      setStatus(ok ? 'ready' : 'idle');
    });
  };

  useEffect(() => {
    if (!window.__mprLoadProgress) window.__mprLoadProgress = {};
    const handler = e => {
      const { progressId, progressData } = e.detail || {};
      if (!progressData) return;
      const uid = getUID();
      if (!uid || progressId !== 'StackProgress:' + uid) return;
      const pct = progressData.percentComplete || 0;
      window.__mprLoadProgress[progressId] = pct;
      if (pct >= 100) tryBuild(uid);
    };
    document.addEventListener('StudyLoadingListenerEvents.OnProgress', handler);
    const uid = getUID();
    if (uid) {
      const pct = (window.__mprLoadProgress || {})['StackProgress:' + uid] || 0;
      if (pct >= 100 || isCached(uid)) tryBuild(uid);
      else setStatus('idle');
    }
    return () => document.removeEventListener('StudyLoadingListenerEvents.OnProgress', handler);
  }, [idx, vsd]);

  useEffect(() => {
    setOpen(false);
    setImageIds([]);
    setStatus('idle');
    buildingFor.current = null;
    if (window._imageIdCache) window._imageIdCache = {};
  }, [idx]);

  const handleClick = () => {
    if (status !== 'ready') return;
    setImageIds(getImageIds(vsd, idx));
    setOpen(v => !v);
  };

  if (status !== 'ready') return null;

  const uid = getUID();

  return (
    <>
      <ToolbarButton
        key={id}
        label={label}
        icon={icon}
        onClick={handleClick}
        isActive={isActive || open}
      />
      {open && imageIds && imageIds.length > 0 && uid && (
        <SinglePanel
          imageIds={imageIds}
          displaySetUID={uid}
          orientation={orientation}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

Single2DMPRButton.propTypes = {
  parentContext:        PropTypes.object.isRequired,
  toolbarClickCallback: PropTypes.func.isRequired,
  button:               PropTypes.object.isRequired,
  activeButtons:        PropTypes.array.isRequired,
  isActive:             PropTypes.bool,
};
